/**
 * Jogar com Bonus — dono unico das regras de selecao de carteira.
 *
 * O core vincula a carteira escolhida ao token/sessao de cassino no launch
 * (`wallet_type`), e esse vinculo e imutavel ate o fim da sessao. Do lado do
 * front isso significa: a escolha acontece UMA vez, antes do iframe montar, e
 * viaja como query param da rota do jogo (`?wallet=...`) — assim sobrevive a
 * reload e deep-link, que remontariam a sessao sem carteira nenhuma.
 *
 * Nenhum componente decide regra: todos consomem daqui. Elegibilidade e do
 * BACKEND: cada jogo chega com `bonus_eligible`, na listagem e no detalhe. Nao
 * ha allowlist no front — lista duplicada aqui envelheceria em silencio e
 * liberaria bonus em jogo que o operador desligou.
 */

/**
 * Vocabulario do core, nao nosso. Valores aceitos pelo `wallet_type` do
 * `/start-game-v2`, conforme a spec de integracao entregue ao front:
 * `cash | bonus`, default `cash`.
 *
 * `cash`  = carteira real (`Wallet.credit`, sacavel, botao laranja).
 * `bonus` = carteira de bonus (`BonusWallet.credit`, botao verde).
 *
 * ATENCAO — o doc de ARQUITETURA do core usa outro par (`wallet` |
 * `bonus_wallet`, os valores do enum interno e da coluna
 * `casino_tokens.wallet_type`). Os dois documentos existem e discordam. Aqui
 * vale a spec de integracao, porque e ela que descreve a chamada HTTP; se o
 * mapeamento interno vazar para a API, o sintoma e a escolha ser descartada em
 * silencio (`WalletType::tryFrom()` devolve null e o core usa a default) — e a
 * correcao e trocar estes dois valores, nada mais.
 *
 * FONTE UNICA: o tipo, a validacao do `?wallet=` da rota, os botoes de escolha
 * e o parametro do launch derivam todos deste objeto. Se o core renomear um
 * valor, muda aqui e acabou — nenhum componente carrega a string solta.
 */
export const WALLET_TYPE = {
  REAL: 'cash',
  BONUS: 'bonus',
} as const;

export type WalletType = (typeof WALLET_TYPE)[keyof typeof WALLET_TYPE];

/**
 * - `double` — jogo elegivel e usuario com saldo bonus: precisa escolher.
 * - `bonus`  — so faz sentido a carteira de bonus (entrada pela pagina/carteira
 *              de bonus). Reservado para a pagina "Jogar com Bonus".
 * - `single` — nada a escolher: segue o fluxo atual, sem gate.
 */
export type WalletChoiceVariant = 'double' | 'bonus' | 'single';

/** Nome do parametro na rota do jogo (`/games/x/y?wallet=...`). */
export const WALLET_TYPE_QUERY = 'wallet';

/**
 * Nome do parametro no launch do core (`/start-game-v2?...&wallet_type=`).
 *
 * SNAKE_CASE, confirmado por curl contra o homolog-4 em 14/08/2026. NAO troque
 * por `walletType`: em camelCase a request devolve 200 para QUALQUER valor
 * (inclusive lixo) porque o Laravel ignora o param desconhecido — a carteira
 * escolhida seria descartada em silencio e todo mundo jogaria com a default.
 * Em snake_case, valor invalido volta 422 com a chave `errors.wallet_type`, que
 * e a prova de qual nome o core realmente valida.
 */
export const WALLET_TYPE_PARAM = 'wallet_type';

const WALLET_TYPES: WalletType[] = Object.values(WALLET_TYPE);

export default function usePlayWithBonus() {
  const { isPlayWithBonusEnabled: isFlagEnabled } = useFeatureFlagsJson();
  const { isAuthenticated } = storeToRefs(useAuthStore());
  const { walletHasBonus, walletBonusFormatted, walletBonusNumber } =
    useWallet();

  const walletStore = useWalletStore();
  const {
    hasLoadedWallet,
    isLoading: isWalletLoading,
    bonusWallet,
    rollover: rolloverData,
    hasLoadedRollover,
  } = storeToRefs(walletStore);

  /**
   * Garante que a carteira ja foi buscada antes de qualquer decisao baseada em
   * saldo. Sem isso ha uma corrida real: o launch do jogo dispara no
   * `immediate` do watch de autenticacao, quando `bonusWallet.credit` ainda e
   * o zero inicial — o gate concluiria "sem bonus" e o jogo abriria direto,
   * sempre.
   */
  async function ensureWalletLoaded(): Promise<void> {
    if (!isAuthenticated.value || hasLoadedWallet.value) return;

    if (!isWalletLoading.value) {
      await walletStore.getWallet();

      return;
    }

    // Fetch ja em andamento (disparado por outro ponto do app): espera ele.
    await new Promise<void>((resolve) => {
      const stop = watch(
        [hasLoadedWallet, isWalletLoading],
        ([loaded, loading]) => {
          if (loaded || !loading) {
            stop();
            resolve();
          }
        },
      );
    });
  }

  function isValidWalletType(value: unknown): value is WalletType {
    return WALLET_TYPES.includes(value as WalletType);
  }

  /** Le o `?wallet=` da rota (string | string[] | undefined do vue-router). */
  function parseWalletType(value: unknown): WalletType | null {
    const raw = Array.isArray(value) ? value[0] : value;

    return isValidWalletType(raw) ? raw : null;
  }

  const isPlayWithBonusEnabled = computed<boolean>(() => {
    return isFlagEnabled.value;
  });

  /**
   * Elegibilidade do JOGO, independente do usuario. Quem decide e o backend,
   * via `bonus_eligible` no objeto do jogo (listagem, categoria e detalhe).
   *
   * Ausente (`undefined`) e tratado como NAO elegivel, nunca como "libera": um
   * payload velho de cache, um endpoint que ainda nao devolve o campo ou um
   * fallback JSON antigo nao podem abrir bonus em jogo que o operador nao
   * habilitou. O custo de errar para menos e um botao a menos; para mais, e
   * bonus gasto em jogo proibido.
   *
   * Vem como boolean de verdade — `0`/`1` ou `"true"` NAO contam. Se o backend
   * mandar assim, o sintoma e o botao nunca aparecer, e a correcao e no
   * payload (ou afrouxar aqui, de forma explicita).
   */
  function isGameBonusEligible(game?: Game | null): boolean {
    return game?.bonus_eligible === true;
  }

  /** Elegibilidade do jogo + condicoes do usuario (flag, login, saldo). */
  function canUseBonus(game?: Game | null): boolean {
    return (
      isPlayWithBonusEnabled.value &&
      isAuthenticated.value &&
      walletHasBonus.value &&
      isGameBonusEligible(game)
    );
  }

  function walletChoiceVariant(game?: Game | null): WalletChoiceVariant {
    return canUseBonus(game) ? 'double' : 'single';
  }

  /**
   * `true` enquanto falta resposta para decidir a carteira. Quem oferece a
   * escolha deve travar o botao ate virar `false`.
   *
   * A escolha e IMUTAVEL pela sessao inteira: tocar em "JOGAR" antes do saldo
   * chegar nao mostra um estado desatualizado por um instante — prende o
   * jogador na carteira errada ate ele sair do jogo. Por isso vale segurar o
   * toque, e nao so atualizar a tela quando o dado chegar.
   *
   * Fica `false` de imediato para quem nao tem escolha nenhuma a fazer (flag
   * desligada ou deslogado), para nao atrasar o fluxo de quem nem veria o
   * botao de bonus.
   */
  const isWalletChoiceLoading = computed<boolean>(() => {
    if (!isPlayWithBonusEnabled.value || !isAuthenticated.value) return false;

    return !hasLoadedWallet.value;
  });

  /**
   * Carteira efetiva de um launch, dado o que veio na rota. Espelha o passo 3
   * do fluxo de launch do core ("se pediu bonus e o jogo nao permite / saldo
   * bonus = 0 → forca wallet"): o `?wallet=` e entrada do usuario, e um link
   * salvo, colado ou compartilhado pode pedir bonus num jogo nao elegivel ou
   * depois do saldo zerar.
   *
   * Rebaixa BONUS→REAL apenas com CONHECIMENTO POSITIVO de que nao pode, nunca
   * por falta de dado. A diferenca nao e teorica: na pagina do jogo o
   * `gameDetails` chega por fetch assincrono, enquanto o launch dispara no
   * `immediate` do watch de autenticacao — usar `canUseBonus` aqui rebaixava
   * toda escolha feita no MobilePreview, porque `bonus_eligible` ainda era
   * `undefined` no momento da decisao. Quem escolheu bonus so viu isso quando o
   * jogo abriu na carteira errada.
   *
   * Elegibilidade desconhecida entao segue como pedida, e o core valida (ele e
   * a autoridade: forca `wallet` se o jogo nao permitir). Para EXIBIR o gate
   * continua valendo o oposto — `canUseBonus` exige `bonus_eligible === true`,
   * porque oferecer o botao sem certeza seria prometer o que talvez nao role.
   *
   * Retorna `null` quando nao ha nada escolhido — ai o launch sai sem
   * `wallet_type` e o core resolve por preferencia/default.
   */
  function resolveWalletType(
    value: unknown,
    game?: Game | null,
  ): WalletType | null {
    const requested = parseWalletType(value);

    if (!requested) return null;
    if (requested !== WALLET_TYPE.BONUS) return requested;

    // Rebaixar uma escolha explicita e sempre um evento a explicar: o jogador
    // pediu bonus e vai jogar com saldo real, sem volta ate o fim da sessao.
    // O motivo vai para o console porque "abriu na carteira errada" e
    // indistinguivel a olho nu de "o core ignorou o wallet_type".
    const downgrade = (reason: string) => {
      console.warn(`[playWithBonus] bonus -> cash: ${reason}`);

      return WALLET_TYPE.REAL;
    };

    // Condicoes que nao dependem de fetch nenhum.
    if (!isPlayWithBonusEnabled.value) return downgrade('flag desligada');
    if (!isAuthenticated.value) return downgrade('nao autenticado');

    // Saldo: so decide depois que a carteira foi buscada (`hasLoadedWallet`),
    // senao o zero inicial passaria por "sem bonus".
    if (hasLoadedWallet.value && !walletHasBonus.value) {
      return downgrade('carteira carregada e sem saldo de bonus');
    }

    // Elegibilidade: `false` e resposta, `undefined` e "ainda nao sei".
    if (game && game.bonus_eligible === false) {
      return downgrade(`jogo ${game.slug ?? ''} veio com bonus_eligible=false`);
    }

    return WALLET_TYPE.BONUS;
  }

  /**
   * Busca a regra de rollover do bonus ATIVO do jogador (`GET bonus/rollover`).
   *
   * Separado do `ensureWalletLoaded` de proposito: o saldo decide SE existe
   * escolha de carteira (bloqueante, precisa de await antes do gate), enquanto o
   * rollover e so exibicao. Deixar o launch esperar por ele empurraria o modal
   * para fora do teto de 500ms do PRD sem necessidade.
   */
  async function ensureRolloverLoaded(): Promise<void> {
    if (!isAuthenticated.value || hasLoadedRollover.value) return;

    await walletStore.getRolloverData();
  }

  /**
   * Regra de rollover do bonus do jogador — dado REAL do core, nunca mock.
   *
   * O PRD manda exibir "Rollover XXx (X bets ... para desbloquear)" antes de
   * iniciar partida com bonus, e o rollover e configurado por bonus/promocao
   * com versionamento (bonus ja ativo mantem a regra original). Numero fixo
   * mentiria para quem tem um bonus de outra promocao — e condicao de promocao
   * errada na tela e risco de compliance, nao so bug visual.
   *
   * Formato `feitas/necessarias` para casar com a pagina da carteira, que ja
   * mostra `count / rollover_count` no mesmo dado (BonusWallet.vue).
   */
  const rollover = computed(() => {
    const data = rolloverData.value || ({} as IRollover);

    const multiplier = data.multiplier ?? 0;
    const betsRequired = data.rollover_count ?? 0;
    const betsDone = data.count ?? 0;

    // `success: false` = sem bonus com rollover ativo. Nao ha regra a anunciar.
    const isActive = data.success !== false;

    const summary =
      isActive && (multiplier || betsRequired)
        ? [
            multiplier ? `Rollover ${multiplier}x` : '',
            betsRequired ? `${betsDone}/${betsRequired} apostas` : '',
          ]
            .filter(Boolean)
            .join(' · ')
        : '';

    return {
      isActive,
      multiplier,
      betsRequired,
      betsDone,
      betsRemaining: Math.max(0, betsRequired - betsDone),
      summary,
    };
  });

  /**
   * Rollover do bonus em R$ — valor apostado vs. meta total, pra faixa de
   * rollover do header do jogo (ENG-817, troca a faixa de XP quando a sessão
   * roda em bônus). MESMA conta do card da carteira (`BonusWallet.vue`):
   * `rollover.amount` já vem em centavos do core, e a meta é
   * `credit_hold * multiplier` — não `rollover_count`/`count`, que é
   * CONTAGEM de apostas (o par que o `rollover` acima já expõe), não valor.
   *
   * Portado pra cá em vez de recalculado em cada consumidor (mobile e
   * desktop) pelo mesmo motivo do resto do arquivo: fonte única.
   */
  const rolloverAmount = computed(() => {
    const data = rolloverData.value || ({} as IRollover);

    const betAmount = (data.amount ?? 0) / 100;
    const targetAmount =
      (bonusWallet.value?.credit_hold ?? 0) * (data.multiplier ?? 0);

    // `success: false` (mesma leitura do `rollover` acima) OU meta zerada
    // (`credit_hold` ainda não chegou, ou veio 0 de verdade) = os valores não
    // significam nada nesse estado. Quem exibe a faixa (Bar.vue, [game].vue)
    // tem que checar isso antes de renderizar, senão mostra "R$0,00 DE
    // R$0,00" ou um alvo zerado com aposta em cima.
    const isActive = data.success !== false && targetAmount > 0;

    return { betAmount, targetAmount, isActive };
  });

  /**
   * Validade do bonus (`bonus_wallet_result.expiry_datetime` do
   * `/users/wallet`), exigida pela spec junto do saldo.
   *
   * O core manda `YYYY-MM-DD HH:MM:SS` (separador ESPACO). `new Date()` com
   * espaco e Invalid Date no Safari/iOS — mesma armadilha que o
   * `useDateTimeHelper` trata com um branch dedicado. Por isso normalizamos
   * para ISO antes de parsear, senao a validade sumiria justamente no iPhone.
   */
  const bonusExpiry = computed(() => {
    const raw = (bonusWallet.value?.expiry_datetime || '') as string;

    if (!raw) {
      return { raw: '', date: null, label: '', isExpired: false };
    }

    const parsed = new Date(String(raw).trim().replace(' ', 'T'));

    if (Number.isNaN(parsed.getTime())) {
      return { raw, date: null, label: '', isExpired: false };
    }

    const label = parsed.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    return {
      raw,
      date: parsed,
      // "Válido até 20/08/2026, 23:59" — data E hora, porque o campo tem hora e
      // um bonus que expira hoje as 23:59 nao pode parecer valido o dia todo.
      label,
      isExpired: parsed.getTime() <= Date.now(),
    };
  });

  return {
    WALLET_TYPE,
    WALLET_TYPE_QUERY,
    WALLET_TYPE_PARAM,
    isPlayWithBonusEnabled,
    isGameBonusEligible,
    canUseBonus,
    walletChoiceVariant,
    isValidWalletType,
    isWalletChoiceLoading,
    parseWalletType,
    resolveWalletType,
    ensureWalletLoaded,
    ensureRolloverLoaded,
    rollover,
    rolloverAmount,
    bonusExpiry,
    walletBonusFormatted,
    walletBonusNumber,
  };
}
